import React, { useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import copy from 'copy-to-clipboard';
import { Copy, Share } from '@menuzen/icon/build/outline';
import config from '../../config/config';
import { Button } from '@mui/material';
import { selectText } from '../../shared/utils/select-text';
import ZenLoading from '../../components/loading/Loading';
import ShareModal from './ShareModal';
import { useGetSiteDesignQuery, useGetAccountQuery } from '../../generated/types';
import DesignPreview from './DesignPreview';
import { enqueueSnackbar } from 'notistack';

const PublishSuccessPage: React.FC = () => {
    const history = useHistory();
    const linkRef = useRef<HTMLDivElement>(null);
    const { accountId } = useParams<{ accountId: string }>();
    const [openShareModal, setOpenShareModal] = useState(false);

    const { loading: isLoadingDesign, data: design } = useGetSiteDesignQuery();

    const { data: accountData, loading: isLoadingAccount } = useGetAccountQuery({
        variables: {
            id: parseInt(accountId + '', 10),
        },
    });
    const onClickDone = () => {
        if (accountId) {
            history.push(`/account/${accountId}/home`);
        } else {
            history.push(`/business`);
        }
    };

    const url = useMemo(
        () => `https://${config.liveBioDomain}/${accountData?.account?.username}`,
        [accountData?.account],
    );
    const handleClickCopy = () => {
        copy(url);
        enqueueSnackbar('Link Copied!', { variant: 'success' });
        if (linkRef.current) {
            selectText(linkRef.current, 0, url.length);
        }
    };

    const handleClickShare = () => {
        if (typeof navigator.share !== 'undefined') {
            const shareData = {
                url,
            };
            navigator.share(shareData);
        } else {
            setOpenShareModal(true);
        }
    };

    return isLoadingAccount || isLoadingDesign ? (
        <div className="flex items-center justify-center flex-auto">
            <ZenLoading />
        </div>
    ) : (
        <div className="flex flex-col flex-auto py-28 px-30 overflow-y-scroll">
            <>
                <div className="mb-20 text-center">
                    <div className="font-bold text-25 leading-27">Success!</div>
                    <div className="font-bold text-25 leading-27">Your menuzen.site is now live!</div>
                    <div className="text-15 leading-16 text-primary-50 mt-15">
                        You can copy the link to your clipboard and share it wherever you like.
                    </div>
                </div>
                <div className="flex-auto flex flex-col overflow-hidden">
                    <div className="flex-auto flex flex-col rounded-16 p-30 bg-primary-10 overflow-hidden">
                        {design?.site && accountData?.account && (
                            <div className={`flex-auto shadow-?.siteCard rounded-10 overflow-hidden`}>
                                <DesignPreview
                                    account={accountData.account}
                                    designSettings={design.site.designSettings}
                                    data={design.site.elements}
                                />
                            </div>
                        )}
                    </div>
                    <div className="mt-20">
                        <div className="flex items-stretch justify-start">
                            <div className="flex-auto flex items-center bg-primary-10 rounded-7 overflow-hidden">
                                <div
                                    ref={linkRef}
                                    className="select-text whitespace-nowrap overflow-hidden flex-auto text-primary-50 leading-none py-18 pl-12"
                                >
                                    {url}
                                </div>
                                <div
                                    className="flex-none text-22 w-50 flex items-center justify- cursor-pointer"
                                    onClick={handleClickCopy}
                                >
                                    <Copy />
                                </div>
                            </div>
                            <div
                                className="text-16 w-50 flex-none flex flex-col items-center justify-center bg-started ml-10"
                                onClick={handleClickShare}
                            >
                                <Share />
                                <p className="mt-5 text-8 leading-10">Share</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`mt-30`}>
                    <Button
                        size={`large`}
                        color={`primary`}
                        variant={`outlined`}
                        fullWidth={true}
                        onClick={onClickDone}
                    >
                        Done
                    </Button>
                </div>
            </>
            <ShareModal
                title={'Share'}
                showClose
                open={openShareModal}
                onClose={() => setOpenShareModal(false)}
                url={url}
            />
        </div>
    );
};

export default PublishSuccessPage;
